import type { AlertOptions } from '@ionic/core'

import { FLOW_DATA_SCHEMA_VERSION } from './flowSchema'

type JsonRecord = Record<string, unknown>
type PresentAlert = (options: AlertOptions) => void | Promise<void>

export async function copyFlowJsonToClipboard(json: string, presentAlert: PresentAlert) {
	try {
		await navigator.clipboard.writeText(json)
		presentAlert({
			header: 'Recording copied',
			message: 'The recorded game stats JSON has been copied to the clipboard.',
			buttons: [ 'OK' ],
		})
	} catch (error) {
		console.error('Failed to copy flow JSON', error)
		presentAlert({
			header: 'Copy failed',
			message: json,
			buttons: [ 'OK' ],
		})
	}
}

export function presentFlowImportAlert(props: {
	gameId: GameId
	onImport: (flow: FlowData) => void
	presentAlert: PresentAlert
}) {
	const { gameId, onImport, presentAlert } = props
	presentAlert({
		header: 'Import recording',
		message: 'Paste JSON copied from another game Export.',
		inputs: [
			{
				name: 'json',
				type: 'textarea',
				placeholder: 'Paste exported JSON',
			},
		],
		buttons: [
			{
				text: 'Cancel',
				role: 'cancel',
			},
			{
				text: 'Import',
				handler: input => {
					try {
						const json = getAlertInputValue(input, 'json')
						onImport(parseImportedFlowJson(json, gameId))
						window.setTimeout(() => {
							presentAlert({
								header: 'Recording imported',
								message: 'The pasted game stats have been imported and will sync to this game.',
								buttons: [ 'OK' ],
							})
						}, 0)
					} catch (error) {
						const message = error instanceof Error
							? error.message
							: 'The pasted JSON could not be imported.'
						window.setTimeout(() => {
							presentAlert({
								header: 'Import failed',
								message,
								buttons: [ 'OK' ],
							})
						}, 0)
					}
				},
			},
		],
	})
}

function getAlertInputValue(input: unknown, name: string) {
	if (!isRecord(input)) {
		return ''
	}

	const value = input[name]
	return typeof value === 'string' ? value : ''
}

function parseImportedFlowJson(json: string, gameId: GameId): FlowData {
	if (!json.trim()) {
		throw new Error('Paste the JSON copied from another game Export.')
	}

	let parsed: unknown
	try {
		parsed = JSON.parse(json)
	} catch {
		throw new Error('The pasted text is not valid JSON.')
	}

	if (!isRecord(parsed)) {
		throw new Error('The pasted JSON is not a game stats export.')
	}

	const now = Date.now()
	return {
		id: gameId,
		schemaVersion: typeof parsed.schemaVersion === 'number' && Number.isFinite(parsed.schemaVersion)
			? parsed.schemaVersion
			: FLOW_DATA_SCHEMA_VERSION,
		created: now,
		updated: now,
		authorUid: null,
		authorSessionId: null,
		setup: parseImportedFlowSetup(parsed.setup),
		currentQuarter: parsePositiveInteger(parsed.currentQuarter, 'currentQuarter'),
		quarterBreaks: parseImportedQuarterBreaks(parsed.quarterBreaks),
		events: parseImportedFlowEvents(parsed.events),
		draftEvent: parsed.draftEvent === null || parsed.draftEvent === undefined
			? null
			: parseImportedDraftEvent(parsed.draftEvent),
	}
}

function parseImportedFlowSetup(value: unknown): FlowData['setup'] {
	if (!isRecord(value)) {
		throw new Error('The pasted JSON is missing setup data.')
	}

	return {
		firstCpTeam: parseFlowTeamSide(value.firstCpTeam, 'setup.firstCpTeam'),
		homeAttackDirection: parseFlowAttackDirection(value.homeAttackDirection, 'setup.homeAttackDirection'),
		homeColor: parseString(value.homeColor, 'setup.homeColor'),
		awayColor: parseString(value.awayColor, 'setup.awayColor'),
	}
}

function parseImportedFlowEvents(value: unknown): FlowEvent[] {
	if (!Array.isArray(value)) {
		throw new Error('The pasted JSON is missing events data.')
	}

	return value.map((event, index) => {
		if (!isRecord(event)) {
			throw new Error(`Event ${index + 1} is not valid.`)
		}

		return {
			cpTeamCode: parseFlowTeamSide(event.cpTeamCode, `events.${index}.cpTeamCode`),
			turnovers: parseTurnovers(event.turnovers, `events.${index}.turnovers`),
			result: parseFlowEventResult(event.result, `events.${index}.result`),
		}
	})
}

function parseImportedDraftEvent(value: unknown): FlowDraftEvent {
	if (!isRecord(value)) {
		throw new Error('The pasted JSON has invalid draft event data.')
	}

	return {
		cpTeamCode: parseFlowTeamSide(value.cpTeamCode, 'draftEvent.cpTeamCode'),
		turnovers: parseTurnovers(value.turnovers, 'draftEvent.turnovers'),
	}
}

function parseImportedQuarterBreaks(value: unknown): number[] {
	if (!Array.isArray(value)) {
		throw new Error('The pasted JSON is missing quarter break data.')
	}

	return value.map((quarterBreak, index) => parseNonNegativeInteger(quarterBreak, `quarterBreaks.${index}`))
}

function parseTurnovers(value: unknown, label: string): FlowEventZone[] {
	if (!Array.isArray(value)) {
		throw new Error(`${label} is not valid.`)
	}

	return value.map((zone, index) => parseFlowEventZone(zone, `${label}.${index}`))
}

function parseFlowTeamSide(value: unknown, label: string): FlowTeamSide {
	if (value === 'home' || value === 'away') {
		return value
	}
	throw new Error(`${label} must be home or away.`)
}

function parseFlowAttackDirection(value: unknown, label: string): FlowAttackDirection {
	if (value === 'left' || value === 'right') {
		return value
	}
	throw new Error(`${label} must be left or right.`)
}

function parseFlowEventZone(value: unknown, label: string): FlowEventZone {
	if (value === 'D' || value === 'M' || value === 'A') {
		return value
	}
	throw new Error(`${label} must be D, M, or A.`)
}

function parseFlowEventResult(value: unknown, label: string): FlowEventResult {
	if (value === 'G' || value === 'GG' || value === 'X' || value === 'I') {
		return value
	}
	throw new Error(`${label} must be G, GG, X, or I.`)
}

function parseString(value: unknown, label: string) {
	if (typeof value === 'string' && value.trim()) {
		return value
	}
	throw new Error(`${label} must be a string.`)
}

function parsePositiveInteger(value: unknown, label: string) {
	if (typeof value === 'number' && Number.isInteger(value) && value >= 1) {
		return value
	}
	throw new Error(`${label} must be a positive integer.`)
}

function parseNonNegativeInteger(value: unknown, label: string) {
	if (typeof value === 'number' && Number.isInteger(value) && value >= 0) {
		return value
	}
	throw new Error(`${label} must be a non-negative integer.`)
}

function isRecord(value: unknown): value is JsonRecord {
	return typeof value === 'object' && value !== null && !Array.isArray(value)
}
